import React, { useState, useEffect } from 'react';
import { API } from '@/App';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, BookOpen } from 'lucide-react';
import axios from 'axios';

/**
 * UnderstandThis — Contextual knowledge entity component
 * Used inline anywhere: state cards, pattern blocks, session screens, lab markers
 * 
 * Props:
 *   state, pattern, symptom, marker, medication — any linked entity key
 *   slug — direct slug lookup (overrides linking)
 *   inline — if true, show as compact inline CTA instead of full card
 */
export function UnderstandThis({ state, pattern, symptom, marker, medication, slug, inline }) {
  const [entities, setEntities] = useState([]);
  const [expanded, setExpanded] = useState(null); // slug of expanded entity
  const [level, setLevel] = useState('simple'); // simple | mechanism | advanced
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams();
    if (state) params.set('state', state);
    if (pattern) params.set('pattern', pattern);
    if (symptom) params.set('symptom', symptom);
    if (marker) params.set('marker', marker);
    if (medication) params.set('medication', medication);

    if (slug) {
      setLoading(true);
      axios.get(`${API}/knowledge-entities/by-slug/${slug}`)
        .then(r => setEntities([r.data]))
        .catch(() => setEntities([]))
        .finally(() => setLoading(false));
    } else if (params.toString()) {
      setLoading(true);
      axios.get(`${API}/knowledge-entities/linked?${params}`)
        .then(r => setEntities(r.data?.entities || []))
        .catch(() => setEntities([]))
        .finally(() => setLoading(false));
    }
  }, [state, pattern, symptom, marker, medication, slug]);

  if (loading || entities.length === 0) return null;

  // Inline mode: just a clickable CTA
  if (inline) {
    return (
      <div className="space-y-2" data-testid="understand-this-inline">
        {entities.slice(0, 2).map(ent => (
          <div key={ent.slug} className="w-full text-left" data-testid={`understand-cta-${ent.slug}`}>
            <button
              onClick={() => setExpanded(expanded === ent.slug ? null : ent.slug)}
              className="w-full text-left flex items-center gap-2 py-1"
              type="button"
            >
              <BookOpen className="w-3 h-3 text-[#8C6B55]/50" strokeWidth={1.5} />
              <span className="text-[10px] text-[#8C6B55]/60 font-['Work_Sans'] hover:text-[#8C6B55] transition-colors">
                Understand: {ent.title}
              </span>
              {expanded === ent.slug ? <ChevronDown className="w-2.5 h-2.5 text-[#8A8681]" /> : <ChevronRight className="w-2.5 h-2.5 text-[#8A8681]" />}
            </button>
            {expanded === ent.slug && (
              <ExpandedEntity entity={ent} level={level} setLevel={setLevel} />
            )}
          </div>
        ))}
      </div>
    );
  }

  // Full card mode
  return (
    <div className="space-y-2" data-testid="understand-this">
      {entities.slice(0, 3).map(ent => (
        <div key={ent.slug} className="rounded-xl border border-[#EBE8E3] overflow-hidden" data-testid={`entity-card-${ent.slug}`}>
          <button
            onClick={() => setExpanded(expanded === ent.slug ? null : ent.slug)}
            className="w-full flex items-center gap-3 p-3.5 text-left hover:bg-[#F8F6F3] transition-colors"
          >
            <BookOpen className="w-4 h-4 text-[#8C6B55]" strokeWidth={1.25} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-['Work_Sans'] font-medium text-[#2C2A29]">{ent.title}</p>
              <p className="text-[10px] text-[#8A8681] font-['Work_Sans'] capitalize">{ent.category}</p>
            </div>
            {expanded === ent.slug ? <ChevronDown className="w-3.5 h-3.5 text-[#8A8681]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#8A8681]" />}
          </button>
          {expanded === ent.slug && (
            <ExpandedEntity entity={ent} level={level} setLevel={setLevel} />
          )}
        </div>
      ))}
    </div>
  );
}

function ExpandedEntity({ entity, level, setLevel }) {
  const text = level === 'advanced' ? entity.advanced : level === 'mechanism' ? entity.mechanism : entity.simple;

  return (
    <div className="px-4 pb-4 border-t border-[#EBE8E3] pt-3 animate-in fade-in slide-in-from-top-1 duration-200" data-testid={`entity-expanded-${entity.slug}`}>
      {/* Level tabs */}
      <div className="flex gap-1 mb-3">
        {[
          { key: 'simple', label: 'Simple' },
          { key: 'mechanism', label: 'Mechanism' },
          ...(entity.advanced ? [{ key: 'advanced', label: 'Advanced' }] : []),
        ].map(l => (
          <button
            key={l.key}
            onClick={(e) => { e.stopPropagation(); setLevel(l.key); }}
            className={`px-2.5 py-1 rounded-full text-[10px] font-['Work_Sans'] transition-colors ${
              level === l.key ? 'bg-[#8C6B55]/10 text-[#8C6B55]' : 'text-[#8A8681] hover:text-[#5C5955]'
            }`}
            data-testid={`level-${l.key}`}
          >
            {l.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <p className="text-[13px] text-[#4A4745] font-['Work_Sans'] leading-[1.8] font-light">{text || entity.simple}</p>

      {/* Linked tags */}
      {(entity.linked_states?.length > 0 || entity.linked_patterns?.length > 0) && (
        <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-[#EBE8E3]">
          {entity.linked_states?.map(s => (
            <Badge key={s} variant="outline" className="text-[8px] border-[#EBE8E3] text-[#8A8681] capitalize">{s.replace('_', ' ')}</Badge>
          ))}
          {entity.linked_patterns?.map(p => (
            <Badge key={p} variant="outline" className="text-[8px] border-[#8C6B55]/20 text-[#8C6B55] capitalize">{p.replace(/_/g, ' ')}</Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export default UnderstandThis;
